import { useCallback, useEffect, useState } from "react";
import { Link2, Plus, Search, Trash2 } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";

const EMPTY = { material: "", batch_number: "", quantity: "", supplier: "", received_date: "" };

export const BatchesTab = () => {
    const [batches, setBatches] = useState([]);
    const [orders, setOrders] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [linkForm, setLinkForm] = useState({});
    const [traceQuery, setTraceQuery] = useState("");
    const [traceResult, setTraceResult] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [b, o] = await Promise.all([
                platformClient.get("/batches"),
                platformClient.get("/production/orders"),
            ]);
            setBatches(b.data);
            setOrders(o.data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const flash = (msg) => {
        setNotice(msg);
        setTimeout(() => setNotice(""), 5000);
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/batches", {
                ...form,
                quantity: Number(form.quantity) || 0,
            });
            setForm(EMPTY);
            flash("Batch geregistreerd");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (b) => {
        if (!window.confirm(`Batch ${b.batch_number} verwijderen?`)) return;
        try {
            await platformClient.delete(`/batches/${b.id}`);
            load();
        } catch {}
    };

    const linkToOrder = async (b) => {
        const f = linkForm[b.id] || {};
        if (!f.order_id || !f.quantity) return;
        const order = orders.find((o) => o.id === f.order_id);
        if (!order) return;
        setError("");
        try {
            await platformClient.patch(`/production/orders/${order.id}`, {
                used_batches: [
                    ...(order.used_batches || []),
                    { material: b.material, batch_number: b.batch_number, quantity: Number(f.quantity) },
                ],
            });
            setLinkForm((s) => ({ ...s, [b.id]: {} }));
            flash(`Batch ${b.batch_number} gekoppeld aan ${order.order_number}`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const trace = async (e) => {
        e.preventDefault();
        if (!traceQuery.trim()) return;
        setError("");
        try {
            const { data } = await platformClient.get(`/batches/trace/${encodeURIComponent(traceQuery.trim())}`);
            setTraceResult(data);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    return (
        <div data-testid="batches-tab" className="mt-8">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="border border-white/10 bg-coal p-6">
                    <p className={labelCls}>Nieuwe grondstof-batch (HACCP)</p>
                    <form data-testid="batch-form" onSubmit={submit} className="mt-4 grid grid-cols-2 gap-5">
                        <label className="block">
                            <span className={labelCls}>Materiaal</span>
                            <input
                                data-testid="batch-form-material"
                                value={form.material}
                                onChange={(e) => setForm({ ...form, material: e.target.value })}
                                required maxLength={120}
                                placeholder="bijv. Aardappelschijfjes"
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Batchnummer</span>
                            <input
                                data-testid="batch-form-number"
                                value={form.batch_number}
                                onChange={(e) => setForm({ ...form, batch_number: e.target.value })}
                                required maxLength={60}
                                placeholder="bijv. AGR-2026-078"
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Hoeveelheid ontvangen</span>
                            <input
                                data-testid="batch-form-quantity"
                                type="number" min="0" step="any"
                                value={form.quantity}
                                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Leverancier</span>
                            <input
                                data-testid="batch-form-supplier"
                                value={form.supplier}
                                onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                                maxLength={120}
                                className={inputCls}
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Ontvangstdatum</span>
                            <input
                                data-testid="batch-form-date"
                                type="date"
                                value={form.received_date}
                                onChange={(e) => setForm({ ...form, received_date: e.target.value })}
                                className={`${inputCls} [color-scheme:dark]`}
                            />
                        </label>
                        <div className="flex items-end">
                            <button
                                data-testid="batch-form-submit"
                                type="submit"
                                disabled={busy}
                                className="flex w-full items-center justify-center gap-2 bg-gold px-5 py-3 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                            >
                                <Plus className="h-4 w-4" /> {busy ? "Opslaan…" : "Registreer"}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="border border-white/10 bg-coal p-6">
                    <p className={labelCls}>Traceer een batch</p>
                    <form data-testid="trace-form" onSubmit={trace} className="mt-4 flex gap-3">
                        <input
                            data-testid="trace-input"
                            value={traceQuery}
                            onChange={(e) => setTraceQuery(e.target.value)}
                            placeholder="Batchnummer, bijv. RVS-4471"
                            className={`${inputCls} mt-0`}
                        />
                        <button
                            data-testid="trace-submit"
                            type="submit"
                            className="flex shrink-0 items-center gap-2 border border-gold/50 px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                        >
                            <Search className="h-3.5 w-3.5" /> Traceer
                        </button>
                    </form>
                    {traceResult ? (
                        <div data-testid="trace-result" className="mt-5">
                            {traceResult.batches.map((b) => (
                                <p key={b.id} className="text-xs text-ash">
                                    Batch <span className="text-gold">{b.batch_number}</span> · {b.material} · {b.quantity} ontvangen
                                    {b.supplier ? ` · ${b.supplier}` : ""}{b.received_date ? ` · ${b.received_date}` : ""}
                                </p>
                            ))}
                            {traceResult.orders.length ? (
                                <div className="mt-3 space-y-2">
                                    {traceResult.orders.map((o) => (
                                        <div key={o.order_id} data-testid={`trace-order-${o.order_id}`} className="flex flex-wrap items-center justify-between gap-2 border border-white/10 bg-ink px-4 py-2.5 text-xs">
                                            <span className="text-bone">
                                                <span className="text-gold">{o.order_number}</span> — {o.customer}
                                            </span>
                                            <span className="text-ash">
                                                {o.quantity_used} gebruikt · {o.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="mt-3 text-xs text-ash">Geen productieorders gevonden met deze batch.</p>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>

            {error ? (
                <p data-testid="batches-error" className="mt-4 border border-red-400/30 bg-red-400/5 px-4 py-3 text-xs text-red-300">{error}</p>
            ) : null}
            {notice ? (
                <p data-testid="batches-notice" className="mt-4 border border-gold/30 bg-gold/5 px-4 py-3 text-xs uppercase tracking-[0.15em] text-gold">{notice}</p>
            ) : null}

            <div className="mt-8 border border-white/10 bg-coal p-6">
                <p className={labelCls}>Geregistreerde batches ({batches.length})</p>
                {batches.length === 0 ? (
                    <p data-testid="batches-empty" className="mt-4 text-sm text-ash">
                        Nog geen batches. Registreer je eerste grondstof-batch hierboven.
                    </p>
                ) : (
                    <div className="mt-4 space-y-3">
                        {batches.map((b) => (
                            <div key={b.id} data-testid={`batch-row-${b.id}`} className="border border-white/10 bg-ink p-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <p className="text-sm text-bone">
                                            <span className="text-gold">{b.batch_number}</span> — {b.material}
                                        </p>
                                        <p className="mt-1 text-xs text-ash">
                                            {b.supplier || "—"}{b.received_date ? ` · ${b.received_date}` : ""} · ontvangen {b.quantity} · gebruikt {b.used ?? 0} · <span className={b.remaining < 0 ? "text-red-300" : "text-bone"}>rest {b.remaining}</span>
                                        </p>
                                    </div>
                                    <button
                                        data-testid={`batch-delete-${b.id}`}
                                        onClick={() => remove(b)}
                                        aria-label="Batch verwijderen"
                                        className="text-ash/50 transition-colors hover:text-red-400"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                    <select
                                        data-testid={`batch-link-order-${b.id}`}
                                        value={linkForm[b.id]?.order_id || ""}
                                        onChange={(e) => setLinkForm((s) => ({ ...s, [b.id]: { ...(s[b.id] || {}), order_id: e.target.value } }))}
                                        className="border border-white/15 bg-ink px-3 py-1.5 text-xs text-bone focus:border-gold focus:outline-none"
                                    >
                                        <option value="">— koppel aan order —</option>
                                        {orders.map((o) => (
                                            <option key={o.id} value={o.id}>
                                                {o.order_number} — {o.customer}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        data-testid={`batch-link-qty-${b.id}`}
                                        type="number" min="0" step="any"
                                        placeholder="aantal"
                                        value={linkForm[b.id]?.quantity || ""}
                                        onChange={(e) => setLinkForm((s) => ({ ...s, [b.id]: { ...(s[b.id] || {}), quantity: e.target.value } }))}
                                        className="w-24 border-b border-white/20 bg-transparent pb-1 text-xs text-bone placeholder:text-ash/50 focus:border-gold focus:outline-none"
                                    />
                                    <button
                                        data-testid={`batch-link-submit-${b.id}`}
                                        onClick={() => linkToOrder(b)}
                                        className="flex items-center gap-1.5 border border-gold/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black"
                                    >
                                        <Link2 className="h-3 w-3" /> Koppel
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
